'use client';

import { Button } from '@zillow/constellation-10';
import { MessageSquare, Phone } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { LucideIcon } from '@/src/components/LucideIcon';
import { useContactFormAction } from '@/src/components/features/property/ContactForm';
import { emitTrackPhoneLeadEvent } from '@/src/components/features/property/lib/analytics';
import { callButtonWrapper, footerActions, messageButtonWrapper } from './propertyManagerModule.styles';

type Props = {
  listing: PropertyDetailListing;
  onMessageClick?: () => void;
};

/**
 * Footer CTAs for the Property Manager module.
 *
 * Message button opens the ContactForm overlay. Call button hits a `tel:`
 * link on the listing's contact phone.
 */
export const PropertyManagerFooter = ({ listing, onMessageClick }: Props) => {
  const t = useTranslations('hdp');
  const { openContactForm, contactFormOverlay, identityResolved } = useContactFormAction(listing);
  const contactPhone = listing?.listedBy?.contactPhone;
  const handleMessageClick = onMessageClick ?? openContactForm;

  if (!listing || listing.active === false) return null;

  const handleCall = () => {
    if (contactPhone) {
      emitTrackPhoneLeadEvent({ listing });
      window.location.href = `tel:${contactPhone.replace(/\D/g, '')}`;
    }
  };

  return (
    <div className={footerActions}>
      <div className={messageButtonWrapper}>
        <Button
          tone="brand"
          emphasis="filled"
          fluid={true}
          icon={<LucideIcon icon={MessageSquare} size="sm" />}
          onClick={handleMessageClick}
          disabled={!onMessageClick && !identityResolved}
          aria-busy={!onMessageClick && !identityResolved}
          data-testid="button-message-property-manager"
        >
          {t('messageCta')}
        </Button>
      </div>
      {contactPhone && (
        <div className={callButtonWrapper}>
          <Button
            tone="brand"
            emphasis="outlined"
            fluid={true}
            icon={<LucideIcon icon={Phone} size="sm" />}
            onClick={handleCall}
            data-testid="button-call"
          >
            {t('callCta')}
          </Button>
        </div>
      )}
      {onMessageClick ? null : contactFormOverlay}
    </div>
  );
};
